// Bileli · app/router — the TYPED ROUTE TABLE on `path-to-regexp` (the same matching engine react-router
// uses; react-router itself is React-bound and can't run in Teact, and history-owning routers would fight
// Telegram's useHistoryBack — so matching is library-powered while the history seam stays untouched).
// ⚠ The path patterns are the PUBLIC URL CONTRACT — they must stay byte-identical to the historical hashes.

import { compile, match } from 'path-to-regexp';

import type { BileliActiveMode } from '../../../util/bileli/types';
import type { BileliContext, BileliContextKind } from '../store/bileliContext.types';

import { isHomeKind } from '../store/bileliContext.types';

export type BileliRouteMode = Extract<BileliActiveMode, 'learn' | 'teach'>;

export interface RouteDef {
  name: string;
  /** path-to-regexp pattern (leading slash added internally; hashes have none). */
  path: string;
  mode: BileliRouteMode;
  toContext: (params: Record<string, string>) => BileliContext;
  fromContext?: (ctx: BileliContext) => Record<string, string> | undefined; // undefined → not this route
  /**
   * Bileli: the Back-PARENT of this screen, declared in the table (single source of truth for the
   * nav-stack's reload seeding). Absent on a drill-in → the mode home; home kinds are roots (parentOf
   * returns undefined for them regardless).
   */
  parent?: (ctx: BileliContext, mode: BileliRouteMode) => BileliContext;
}

// Order matters: first match wins (specific before generic).
const ROUTES: RouteDef[] = [
  {
    name: 'learn-course',
    path: '/learn/course/:courseId',
    mode: 'learn',
    toContext: (p) => ({ kind: 'course', courseId: p.courseId }),
    fromContext: (c) => (c.kind === 'course' && c.courseId ? { courseId: c.courseId } : undefined),
    parent: () => ({ kind: 'catalog' }),
  },
  {
    name: 'catalog',
    path: '/learn/catalog',
    mode: 'learn',
    toContext: () => ({ kind: 'catalog' }),
    fromContext: (c) => (c.kind === 'catalog' ? {} : undefined),
  },
  {
    // The profile page is BI-MODAL (one kind, one screen) — a route per mode keeps the URL honest about
    // which mode it was opened from, and buildHash picks the current mode's variant automatically.
    name: 'learn-profile',
    path: '/learn/profile',
    mode: 'learn',
    toContext: () => ({ kind: 'profile' }),
    fromContext: (c) => (c.kind === 'profile' ? {} : undefined),
  },
  {
    name: 'my-learning',
    path: '/learn/my',
    mode: 'learn',
    toContext: () => ({ kind: 'my-learning' }),
    fromContext: (c) => (c.kind === 'my-learning' ? {} : undefined),
  },
  {
    // Bileli: student order history (Finik) — literal learn path, reached from the profile panel.
    name: 'my-orders',
    path: '/learn/orders',
    mode: 'learn',
    toContext: () => ({ kind: 'my-orders' }),
    fromContext: (c) => (c.kind === 'my-orders' ? {} : undefined),
  },
  {
    // Student SRS practice home (the card loop is transient in-page state, not a route).
    name: 'practice',
    path: '/learn/practice',
    mode: 'learn',
    toContext: () => ({ kind: 'practice' }),
    fromContext: (c) => (c.kind === 'practice' ? {} : undefined),
  },
  {
    // Public program landing (PR #184) — banner tap / program links. Slug is server-generated.
    name: 'learn-program',
    path: '/learn/program/:programSlug',
    mode: 'learn',
    toContext: (p) => ({ kind: 'program', programSlug: p.programSlug }),
    fromContext: (c) => (c.kind === 'program' && c.programSlug ? { programSlug: c.programSlug } : undefined),
  },
  {
    // Public provider (org) profile — BI-MODAL like profile: a route per mode keeps the URL honest.
    name: 'learn-provider',
    path: '/learn/provider/:orgSlug',
    mode: 'learn',
    toContext: (p) => ({ kind: 'provider', orgSlug: p.orgSlug }),
    fromContext: (c) => (c.kind === 'provider' && c.orgSlug ? { orgSlug: c.orgSlug } : undefined),
  },
  {
    name: 'learn-home',
    path: '/learn',
    mode: 'learn',
    toContext: () => ({ kind: 'dashboard' }),
    fromContext: (c) => (c.kind === 'dashboard' ? {} : undefined),
  },
  {
    name: 'module-draft',
    path: '/teach/course/:courseId/module/:moduleId/draft',
    mode: 'teach',
    toContext: (p) => ({ kind: 'module', courseId: p.courseId, moduleId: p.moduleId, isDraft: true }),
    fromContext: (c) => (c.kind === 'module' && c.isDraft && c.courseId && c.moduleId
      ? { courseId: c.courseId, moduleId: c.moduleId } : undefined),
    parent: (c) => ({ kind: 'module', courseId: c.courseId, moduleId: c.moduleId }),
  },
  {
    name: 'module-lesson',
    path: '/teach/course/:courseId/module/:moduleId/lesson/:lessonId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'module', courseId: p.courseId, moduleId: p.moduleId, lessonId: p.lessonId }),
    fromContext: (c) => (c.kind === 'module' && !c.isDraft && c.courseId && c.moduleId && c.lessonId
      ? { courseId: c.courseId, moduleId: c.moduleId, lessonId: c.lessonId } : undefined),
    parent: (c) => ({ kind: 'module', courseId: c.courseId, moduleId: c.moduleId }),
  },
  {
    name: 'module',
    path: '/teach/course/:courseId/module/:moduleId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'module', courseId: p.courseId, moduleId: p.moduleId }),
    fromContext: (c) => (c.kind === 'module' && !c.isDraft && c.courseId && c.moduleId && !c.lessonId
      ? { courseId: c.courseId, moduleId: c.moduleId } : undefined),
    parent: (c) => ({ kind: 'editor', courseId: c.courseId }),
  },
  {
    // Historical: the student lesson player rides the `teach/...` prefix (kept byte-identical).
    name: 'lesson',
    path: '/teach/course/:courseId/lesson/:lessonId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'lesson', courseId: p.courseId, lessonId: p.lessonId }),
    fromContext: (c) => (c.kind === 'lesson' && c.courseId && c.lessonId
      ? { courseId: c.courseId, lessonId: c.lessonId } : undefined),
    // Bi-modal: the player's Back-parent is the course landing under learn, the editor under teach.
    parent: (c, mode) => (mode === 'learn'
      ? { kind: 'course', courseId: c.courseId }
      : { kind: 'editor', courseId: c.courseId }),
  },
  {
    name: 'course-editor',
    path: '/teach/course/:courseId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'editor', courseId: p.courseId }),
    fromContext: (c) => (c.kind === 'editor' && c.courseId ? { courseId: c.courseId } : undefined),
  },
  {
    // Bileli: teacher drain screen (course-space provisioning) — new additive path, teach-pinned.
    name: 'spaces',
    path: '/teach/spaces',
    mode: 'teach',
    toContext: () => ({ kind: 'spaces' }),
    fromContext: (c) => (c.kind === 'spaces' ? {} : undefined),
  },
  {
    // Bileli: teacher earnings & payouts (Finik) — literal teach path, can't collide with other routes.
    name: 'earnings',
    path: '/teach/earnings',
    mode: 'teach',
    toContext: () => ({ kind: 'earnings' }),
    fromContext: (c) => (c.kind === 'earnings' ? {} : undefined),
  },
  {
    // Bileli: «Команда» — school staff (roster + invite links) + org settings, moved OFF the profile
    // page for findability (user ask 2026-07-15). Literal teach path like spaces/earnings.
    name: 'team',
    path: '/teach/team',
    mode: 'teach',
    toContext: () => ({ kind: 'team' }),
    fromContext: (c) => (c.kind === 'team' ? {} : undefined),
  },
  {
    // Teacher analytics: per-student 360° (most specific of the /teach/students family — order matters).
    name: 'student-detail',
    path: '/teach/students/cohort/:cohortId/student/:studentId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'student', cohortId: p.cohortId, studentId: p.studentId }),
    fromContext: (c) => (c.kind === 'student' && c.cohortId && c.studentId
      ? { cohortId: c.cohortId, studentId: c.studentId } : undefined),
    parent: () => ({ kind: 'students' }),
  },
  {
    // Teacher analytics: a course's student roster (the cohort is picked in-page).
    name: 'roster',
    path: '/teach/students/course/:courseId',
    mode: 'teach',
    toContext: (p) => ({ kind: 'roster', courseId: p.courseId }),
    fromContext: (c) => (c.kind === 'roster' && c.courseId ? { courseId: c.courseId } : undefined),
    parent: () => ({ kind: 'students' }),
  },
  {
    // Teacher analytics home: overview KPIs + course list.
    name: 'students',
    path: '/teach/students',
    mode: 'teach',
    toContext: () => ({ kind: 'students' }),
    fromContext: (c) => (c.kind === 'students' ? {} : undefined),
  },
  {
    // Teacher live sessions (эфиры) for one course. Order vs the earlier /teach/course/:courseId routes
    // is safe: path-to-regexp matches exactly, so the extra /live segment can't hit course-editor.
    name: 'live',
    path: '/teach/course/:courseId/live',
    mode: 'teach',
    toContext: (p) => ({ kind: 'live', courseId: p.courseId }),
    fromContext: (c) => (c.kind === 'live' && c.courseId ? { courseId: c.courseId } : undefined),
    parent: (c) => ({ kind: 'editor', courseId: c.courseId }),
  },
  {
    // Teacher program studio home (PR #184). Literal `/teach/programs` cannot collide with
    // `/teach/program/:programSlug` below — path-to-regexp matches segments exactly.
    name: 'teacher-programs',
    path: '/teach/programs',
    mode: 'teach',
    toContext: () => ({ kind: 'teacher-programs' }),
    fromContext: (c) => (c.kind === 'teacher-programs' ? {} : undefined),
  },
  {
    name: 'program-editor',
    path: '/teach/program/:programSlug',
    mode: 'teach',
    toContext: (p) => ({ kind: 'program-editor', programSlug: p.programSlug }),
    fromContext: (c) => (c.kind === 'program-editor' && c.programSlug
      ? { programSlug: c.programSlug } : undefined),
    parent: () => ({ kind: 'teacher-programs' }),
  },
  {
    // The bi-modal provider twin (teach-side URL) — see learn-provider.
    name: 'teach-provider',
    path: '/teach/provider/:orgSlug',
    mode: 'teach',
    toContext: (p) => ({ kind: 'provider', orgSlug: p.orgSlug }),
    fromContext: (c) => (c.kind === 'provider' && c.orgSlug ? { orgSlug: c.orgSlug } : undefined),
  },
  {
    name: 'teach-profile',
    path: '/teach/profile',
    mode: 'teach',
    toContext: () => ({ kind: 'profile' }),
    fromContext: (c) => (c.kind === 'profile' ? {} : undefined),
  },
  {
    name: 'teach-home',
    path: '/teach',
    mode: 'teach',
    toContext: () => ({ kind: 'teacher-courses' }),
    fromContext: (c) => (c.kind === 'teacher-courses' ? {} : undefined),
  },
];

const matchers = ROUTES.map((def) => ({ def, matcher: match(def.path), builder: compile(def.path) }));

/** Bileli: test-facing view of the table — the routes round-trip spec iterates EVERY def. */
export const ROUTE_DEFS: readonly RouteDef[] = ROUTES;

/** The default (root) home context of a mode. */
export function homeContextOf(mode: BileliRouteMode): BileliContext {
  return { kind: mode === 'teach' ? 'teacher-courses' : 'dashboard' };
}

/**
 * STANDALONE (mode-less) deep links — checked by the rail's restore effect BEFORE parseBileliHash.
 * `org-invite`: the invite token rides the FRAGMENT (never sent to any server; the util/routing
 * module-load snapshot preserves it across the whole Telegram sign-in flow).
 */
export function matchStandaloneHash(raw: string | undefined): { name: 'org-invite'; token: string } | undefined {
  if (!raw) return undefined;
  const clean = raw.replace(/^#/, '').trim();
  const m = /^org-invite\/(.+)$/.exec(clean);
  return m ? { name: 'org-invite', token: m[1] } : undefined;
}

/**
 * Bileli: does this hash CLAIM to be a Bileli education route (`learn`/`teach` first segment)?
 * Scoped so unknown-hash handling (404 toast) never fires for foreign prefixes — e.g. the
 * standalone `#org-invite/<token>` deep link, or Telegram's own numeric chat hashes.
 */
export function isBileliLikeHash(raw: string | undefined): boolean {
  if (!raw) return false;
  const clean = raw.replace(/^#/, '').trim();
  return /^(learn|teach)($|\/)/.test(clean);
}

export interface MatchedRoute {
  name: string;
  mode: BileliRouteMode;
  context: BileliContext;
}

/** Match a location hash (no leading '#') against the route table. */
export function matchHash(raw: string | undefined): MatchedRoute | undefined {
  if (!raw) return undefined;
  const clean = raw.replace(/^#/, '').trim();
  if (!clean) return undefined;
  const path = `/${clean}`;
  for (const { def, matcher } of matchers) {
    const m = matcher(path);
    if (m) {
      return { name: def.name, mode: def.mode, context: def.toContext(m.params as Record<string, string>) };
    }
  }
  return undefined;
}

/** Build a hash from the defs of ONE mode; undefined when none represents the context. */
function buildForMode(mode: BileliRouteMode, context: BileliContext): string | undefined {
  for (const { def, builder } of matchers) {
    if (def.mode !== mode) continue;
    const params = def.fromContext?.(context);
    if (params) return builder(params).slice(1); // drop the leading '/'
  }
  return undefined;
}

/** Build the hash (no leading '#') for a mode + context; undefined when no route represents it. */
export function buildHash(mode: BileliActiveMode, context: BileliContext | undefined): string | undefined {
  if (mode !== 'learn' && mode !== 'teach') return undefined;
  if (!context) return mode === 'teach' ? 'teach' : 'learn';
  // Bileli: retry the OTHER mode before falling back — a bi-modal kind whose only URL lives in one mode
  // (the lesson player rides `teach/...`) must still get its real hash when opened under the other mode.
  // The final home fallback stays as the safety net; the routes round-trip test is the drift gate.
  return buildForMode(mode, context)
    ?? buildForMode(mode === 'teach' ? 'learn' : 'teach', context)
    ?? (mode === 'teach' ? 'teach' : 'learn');
}

/**
 * Bileli: the Back-PARENT of a context (for the nav-stack's reload seeding) — home kinds are roots
 * (undefined), drill-ins use their route's declared `parent`, defaulting to the mode home.
 */
export function parentOf(context: BileliContext, mode: BileliRouteMode): BileliContext | undefined {
  if (isHomeKind(context.kind)) return undefined;
  for (const { def } of matchers) {
    if (!def.fromContext?.(context)) continue;
    return def.parent?.(context, mode) ?? homeContextOf(mode);
  }
  return homeContextOf(mode); // unroutable drill-in → seed at least the mode home
}

/**
 * Which mode a context kind belongs to — used for cross-mode guards. `lesson` and `profile` are
 * deliberately absent: they're BI-MODAL (lesson = player under learn / editor under teach; profile =
 * the same account page under either mode, so opening it never flips the mode).
 */
const KIND_MODE: Partial<Record<BileliContextKind, BileliRouteMode>> = {
  dashboard: 'learn',
  catalog: 'learn',
  'my-learning': 'learn',
  'my-orders': 'learn', // student order history (Finik) — reached from the profile panel
  practice: 'learn',
  course: 'learn',
  program: 'learn', // public program landing — teach previews flip the mode explicitly first
  'teacher-courses': 'teach',
  editor: 'teach',
  module: 'teach',
  spaces: 'teach', // pinned — the operator screen must never render under Учёба
  students: 'teach', // teacher analytics home + drill-ins
  roster: 'teach',
  student: 'teach',
  live: 'teach',
  'teacher-programs': 'teach',
  earnings: 'teach', // teacher money screen (Finik payouts) — teach-pinned
  team: 'teach', // school staff & settings — a management screen, teach-pinned
  'program-editor': 'teach',
  // `provider` is deliberately absent: BI-MODAL like profile/lesson — a provider page opens from either
  // mode (org settings' «Открыть страницу школы» in teach, provider links in learn) without a mode flip.
};

export function modeOfKind(kind: BileliContextKind): BileliRouteMode | undefined {
  return KIND_MODE[kind];
}
